"use client";

import * as React from "react";
import { format, parse } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "@baluu/ui/lib/utils";
import { Input } from "@baluu/ui/components/input";
import { Textarea } from "@baluu/ui/components/textarea";
import { Label } from "@baluu/ui/components/label";
import { Checkbox } from "@baluu/ui/components/checkbox";
import { Button } from "@baluu/ui/components/button";
import { Calendar } from "@baluu/ui/components/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@baluu/ui/components/popover";
import { ResponsiveSelect } from "@baluu/ui/components/responsive-select";
import { PhoneCountryInput } from "@baluu/ui/components/phone-country-input";
import {
  StarRatingInput,
  CurrencyInput,
  DateTimeInput,
  UrlInput,
  MultiSelectCheckboxGroup,
  AddressFieldInput,
} from "./field-inputs.tsx";

// Native shape returned by GET /widget/customer-fields — see
// turbo/apps/api/src/services/customer/CustomerField.service.ts
// (`resolveCheckoutFields`). Mirrors that service's CHECKOUTABLE_TYPES, which
// now includes `multiSelect` and the four new checkout types.
type CustomerFieldType =
  | "text"
  | "longText"
  | "number"
  | "date"
  | "dateOfBirth"
  | "select"
  | "multiSelect"
  | "checkbox"
  | "phone"
  | "email"
  | "address"
  | "dateTime"
  | "rating"
  | "url"
  | "currency";

type CheckoutCustomerField = {
  id: string;
  slug: string;
  label: string;
  fieldType: CustomerFieldType;
  helpText: string | null;
  isRequired: boolean;
  options: string[];
};

type CustomerFieldInputProps = {
  field: CheckoutCustomerField;
  value: string;
  onChange: (value: string) => void;
  /** School currency symbol for the `currency` field type (e.g. "£"). */
  currencySymbol?: string;
  /** Seeds the dial code on `phone` fields — pass the school's businessCountry. */
  defaultCountryCode?: string;
  /**
   * Where the date picker's popover goes. Left out, it portals to
   * document.body (the page checkouts). `null` renders it inline, which is
   * what a Shadow DOM host needs: a portal to body leaves the shadow root's
   * styles behind, and Radix misreads clicks inside a shadow root as outside
   * unless the popover is inline (see PopoverContent).
   */
  portalContainer?: HTMLElement | null;
};

/**
 * Parse a multiSelect answer (stored as a JSON-array string in the single
 * Record<slug, string> answers map) back into an array. Falls back to [] on
 * empty/malformed data so a corrupt value never crashes the render.
 */
function parseMultiSelect(value: string): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Renders a single customer-field question, keyed by its native `fieldType`. */
function CustomerFieldInput({
  field,
  value,
  onChange,
  currencySymbol,
  defaultCountryCode,
  portalContainer,
}: CustomerFieldInputProps) {
  const inputId = `customer-field-${field.slug}`;
  const labelText = field.isRequired ? `${field.label} *` : field.label;

  if (field.fieldType === "checkbox") {
    // Stored as the string "true"/"false" (the shared answers state is a
    // single Record<string, string>); converted to a real boolean only when
    // the checkout payload is built.
    const checked = value === "true";
    return (
      <div className="space-y-1">
        <div className="flex items-center space-x-2">
          <Checkbox
            id={inputId}
            checked={checked}
            onCheckedChange={(next) => onChange(next ? "true" : "false")}
          />
          <Label htmlFor={inputId} className="cursor-pointer font-normal">
            {labelText}
          </Label>
        </div>
        {field.helpText && (
          <p className="text-xs text-muted-foreground pl-6">{field.helpText}</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={inputId}>{labelText}</Label>
      {field.fieldType === "text" && (
        <Input id={inputId} value={value} onChange={(e) => onChange(e.target.value)} />
      )}
      {field.fieldType === "email" && (
        <Input
          id={inputId}
          type="email"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {field.fieldType === "phone" && (
        // container={null} renders the country dropdown inline rather than
        // portalling to document.body, which is what makes it work inside the
        // checkout overlay AND the webcomponents Shadow DOM — same reason the
        // select branch below passes it.
        <PhoneCountryInput
          value={value}
          onChange={onChange}
          defaultCountryCode={defaultCountryCode}
          container={null}
        />
      )}
      {field.fieldType === "address" && (
        <AddressFieldInput value={value} onChange={onChange} />
      )}
      {field.fieldType === "longText" && (
        <Textarea
          id={inputId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
        />
      )}
      {field.fieldType === "number" && (
        <NumberFieldInput id={inputId} value={value} onChange={onChange} />
      )}
      {(field.fieldType === "date" || field.fieldType === "dateOfBirth") && (
        <DateFieldInput
          value={value}
          onChange={onChange}
          label={field.label}
          pastOnly={field.fieldType === "dateOfBirth"}
          portalContainer={portalContainer}
        />
      )}
      {field.fieldType === "select" && (
        <ResponsiveSelect
          value={value}
          onValueChange={onChange}
          options={field.options.map((option) => ({ value: option, label: option }))}
          placeholder={`Select ${field.label}`}
          title={field.label}
          container={null}
        />
      )}
      {field.fieldType === "multiSelect" && (
        <MultiSelectCheckboxGroup
          options={field.options}
          value={parseMultiSelect(value)}
          onChange={(selected) => onChange(JSON.stringify(selected))}
        />
      )}
      {field.fieldType === "dateTime" && (
        <DateTimeInput value={value} onChange={onChange} />
      )}
      {field.fieldType === "rating" && (
        <StarRatingInput value={value} onChange={onChange} />
      )}
      {field.fieldType === "url" && (
        <UrlInput value={value} onChange={onChange} />
      )}
      {field.fieldType === "currency" && (
        <CurrencyInput
          value={value}
          onChange={onChange}
          currencySymbol={currencySymbol}
        />
      )}
      {field.helpText && (
        <p className="text-xs text-muted-foreground">{field.helpText}</p>
      )}
    </div>
  );
}

function NumberFieldInput({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  // Raw string in, parse on blur — Number("") is 0, so coercing on every
  // keystroke would snap the field back to 0 the instant it's cleared
  // (project-wide numeric-input rule).
  const [raw, setRaw] = React.useState(value);

  // Guard against clobbering active typing: `value` is the parent's
  // committed answer, which only updates via `onChange` (fired on blur
  // below) — EXCEPT another effect can also write straight into the shared
  // `customerFieldAnswers` state for this same slug (e.g. the saved-values
  // prefill resolving while this field is still empty). If that lands while
  // the user has this input focused and is mid-keystroke, syncing `raw` from
  // `value` would silently overwrite whatever they've typed so far. Track
  // focus so the sync only ever applies while the input is NOT focused —
  // the commit direction (raw -> value) already flows the other way on blur.
  const isFocusedRef = React.useRef(false);

  React.useEffect(() => {
    if (isFocusedRef.current) return;
    setRaw(value);
  }, [value]);

  return (
    <Input
      id={id}
      type="number"
      value={raw}
      onFocus={() => {
        isFocusedRef.current = true;
      }}
      onChange={(e) => setRaw(e.target.value)}
      onBlur={() => {
        isFocusedRef.current = false;
        const trimmed = raw.trim();
        if (trimmed === "") {
          setRaw("");
          onChange("");
          return;
        }
        const parsed = Number(trimmed);
        if (!Number.isFinite(parsed)) {
          setRaw("");
          onChange("");
          return;
        }
        const normalized = String(parsed);
        setRaw(normalized);
        onChange(normalized);
      }}
    />
  );
}

function DateFieldInput({
  value,
  onChange,
  label,
  pastOnly = false,
  portalContainer,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  portalContainer?: HTMLElement | null;
  /** dateOfBirth: today and every future date are rejected server-side, so
   *  don't offer them here either. */
  pastOnly?: boolean;
}) {
  // "yyyy-MM-dd" — same convention the dashboard uses for CustomerField date
  // values (apps/dashboard .../edit-customer-details-popup.tsx), so a value
  // saved from either surface round-trips through the other.
  //
  // Parse with date-fns `parse` (LOCAL time), not `new Date("yyyy-MM-dd")` —
  // a bare date-only string is spec'd as UTC midnight, which shifts a day
  // back in any negative-UTC-offset locale (e.g. the US) once rendered in
  // local time. `parse` builds the Date from the components directly in the
  // local zone, so the round-trip through `format(date, "yyyy-MM-dd")` below
  // is stable in every offset.
  const selectedDate = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  const isValidDate = !!selectedDate && !Number.isNaN(selectedDate.getTime());
  const currentYear = new Date().getFullYear();
  // "after yesterday" disables today AND everything later.
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !isValidDate && "text-muted-foreground",
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {isValidDate ? format(selectedDate!, "PPP") : `Select ${label}`}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" container={portalContainer}>
        <Calendar
          mode="single"
          captionLayout="dropdown"
          fromYear={currentYear - 100}
          toYear={pastOnly ? currentYear : currentYear + 10}
          disabled={pastOnly ? { after: yesterday } : undefined}
          selected={isValidDate ? selectedDate : undefined}
          defaultMonth={
            isValidDate
              ? selectedDate
              : pastOnly
                ? new Date(currentYear - 8, 0, 1)
                : new Date()
          }
          onSelect={(date) => onChange(date ? format(date, "yyyy-MM-dd") : "")}
        />
      </PopoverContent>
    </Popover>
  );
}

type CheckoutCustomerFieldsProps = {
  /** Field definitions resolved for the courses being bought. */
  fields: CheckoutCustomerField[];
  /** slug -> raw string answer. Owned by the page (it builds the checkout payloads). */
  answers: Record<string, string>;
  onAnswerChange: (slug: string, value: string) => void;
  /** School currency symbol for `currency` fields (e.g. "£"). */
  currencySymbol?: string;
  /** Seeds the dial code on `phone` fields — pass the school's businessCountry. */
  defaultCountryCode?: string;
  /** See CustomerFieldInputProps.portalContainer (`null` inside Shadow DOM). */
  portalContainer?: HTMLElement | null;
};

/**
 * The buyer's "Your Information" inputs: one input per resolved customer
 * field. Rendered on the basket checkout page, and by the dashboard's
 * checkout preview so owners see the same block their customers get.
 *
 * The returning-customer recognition banner (`KnownEmailBanner`) is owned by
 * the checkout page — it sits directly under the email in the combined details
 * card, above these inputs — so it is intentionally not rendered here. All data
 * fetching lives in the page; this component is props-in/callbacks-out.
 */
function CheckoutCustomerFields({
  fields,
  answers,
  onAnswerChange,
  currencySymbol,
  defaultCountryCode,
  portalContainer,
}: CheckoutCustomerFieldsProps) {
  if (fields.length === 0) return null;

  return (
    <div className="space-y-4">
      {fields.map((field) => (
        <CustomerFieldInput
          key={field.slug}
          field={field}
          value={answers[field.slug] ?? ""}
          onChange={(value) => onAnswerChange(field.slug, value)}
          currencySymbol={currencySymbol}
          defaultCountryCode={defaultCountryCode}
          portalContainer={portalContainer}
        />
      ))}
    </div>
  );
}

/**
 * Whether a required field counts as answered. Mirrors the per-type rules the
 * basket checkout enforces before payment (and the server's
 * `validateCustomerFieldAnswers`): a stored answer can be non-empty and still
 * fail required — an unticked checkbox is the string "false", and an empty
 * multiSelect is "[]".
 *
 * Presence only, never format: a filled-but-malformed value (a scheme-less
 * url) passes here and is rejected by the checkout's own format check at
 * submit, where it can name the offending field.
 */
function isCustomerFieldAnswered(
  field: CheckoutCustomerField,
  answer: string | undefined,
): boolean {
  switch (field.fieldType) {
    case "checkbox":
      return answer === "true";
    case "multiSelect": {
      // Only options the field still declares count — the payload builder
      // drops since-removed ones, so a field whose every selection was
      // removed would otherwise pass here and send an empty array.
      const declared = new Set(field.options ?? []);
      return parseMultiSelect(answer ?? "").some((option) =>
        declared.has(option),
      );
    }
    case "rating":
      return /^[1-5]$/.test(answer ?? "");
    default:
      return (answer ?? "") !== "";
  }
}

/** True when every REQUIRED field in `fields` has a usable answer. */
function areCustomerFieldAnswersComplete(
  fields: CheckoutCustomerField[],
  answers: Record<string, string>,
): boolean {
  return fields
    .filter((field) => field.isRequired)
    .every((field) => isCustomerFieldAnswered(field, answers[field.slug]));
}

// Re-exported for callers that import this component through the
// `@baluu/ui/components/checkout/*` subpath (which only resolves .tsx files).
export {
  CustomerFieldInput,
  CheckoutCustomerFields,
  isCustomerFieldAnswered,
  areCustomerFieldAnswersComplete,
};
export type {
  CustomerFieldType,
  CheckoutCustomerField,
  CustomerFieldInputProps,
  CheckoutCustomerFieldsProps,
};
